import { useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { trpc } from '@/api/trpc';

interface CanonicalRuleLookupKey {
    serializedContextFeatureRef: string;
    selectorType: string;
    selector: unknown;
}

interface UseScorecardRuleStatusOptions {
    scorecardId: number | null;
    enabled: boolean;
}

export function useScorecardRuleStatus({ scorecardId, enabled }: UseScorecardRuleStatusOptions) {
    // Fetch all rules for the selected scorecard
    const rulesQuery = useQuery({
        ...trpc.scorecards.getRulesByScorecard.queryOptions({
            scorecardId: scorecardId!
        }),
        enabled: enabled && scorecardId !== null,
        staleTime: 300000 // 5 minutes
    });

    const scorecardRules = rulesQuery.data?.rules;

    // Build canonical lookup set for O(1) checks
    const ruleSet = useMemo(() => {
        if (!scorecardRules) return new Set<string>();

        const set = new Set<string>();

        for (const rule of scorecardRules) {
            if (!rule.serializedContextFeatureRef) continue;

            set.add(
                createCanonicalRuleKey({
                    serializedContextFeatureRef: rule.serializedContextFeatureRef,
                    selectorType: rule.selectorType,
                    selector: rule.selector
                })
            );
        }

        return set;
    }, [scorecardRules]);

    const canonicalFeatureCounts = useMemo(() => {
        if (!scorecardRules) return new Map<string, number>();

        const counts = new Map<string, number>();

        for (const rule of scorecardRules) {
            if (!rule.serializedContextFeatureRef) continue;

            counts.set(rule.serializedContextFeatureRef, (counts.get(rule.serializedContextFeatureRef) || 0) + 1);
        }

        return counts;
    }, [scorecardRules]);

    const isCanonicalValueInScorecard = useCallback(
        (params: CanonicalRuleLookupKey): boolean => {
            return ruleSet.has(createCanonicalRuleKey(params));
        },
        [ruleSet]
    );

    const getCanonicalFeatureValueCount = useCallback(
        (serializedContextFeatureRef: string): number => {
            return canonicalFeatureCounts.get(serializedContextFeatureRef) || 0;
        },
        [canonicalFeatureCounts]
    );

    return {
        isLoading: rulesQuery.isLoading,
        isCanonicalValueInScorecard,
        getCanonicalFeatureValueCount
    };
}

function createCanonicalRuleKey(rule: CanonicalRuleLookupKey): string {
    try {
        const selectorObj = typeof rule.selector === 'string' ? JSON.parse(rule.selector) : rule.selector;

        return JSON.stringify({
            ref: rule.serializedContextFeatureRef,
            stype: rule.selectorType,
            sel: selectorObj
        });
    } catch (error) {
        console.error('Failed to create canonical rule key:', error, rule);
        throw new Error(`Invalid selector data for feature ref: ${rule.serializedContextFeatureRef}`, { cause: error });
    }
}
